import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";

export default function AgentLogin() {
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { agentLogin, agent, error } = useAuth();
  const nav = useNavigate();

  useEffect(() => { if (agent) nav("/agent/dashboard"); }, [agent, nav]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const ok = await agentLogin(code.toUpperCase().trim(), password);
    setBusy(false);
    if (ok) nav("/agent/dashboard");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-950 text-white relative overflow-hidden">
        <div className="grain absolute inset-0"/>
        <div className="relative">
          <div className="font-display font-extrabold text-xl">TARA Finserv</div>
          <div className="text-xs uppercase tracking-[0.22em] text-saffron">Agent Portal</div>
        </div>
        <div className="relative">
          <div className="overline text-saffron">Agent code</div>
          <h2 className="font-display text-4xl xl:text-5xl font-extrabold mt-3">Track leads, manage clients, earn payouts — all from one login.</h2>
          <p className="text-slate-400 mt-4 max-w-md">Don't have a code yet? <Link to="/agent/register" className="text-saffron font-semibold">Register as an agent</Link> to get yours.</p>
        </div>
        <div className="relative text-sm text-slate-400">For Loan / Insurance / DSA agents only.</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} autoComplete="off" className="w-full max-w-md card-flat p-8 sm:p-10 relative" data-testid="agent-login-form">
          <Link to="/" data-testid="agent-login-back" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted hover:text-saffron transition-colors mb-5">
            <ArrowLeft className="h-3.5 w-3.5"/> Back to home
          </Link>
          <input type="text" name="fake-user" autoComplete="username" tabIndex={-1} aria-hidden="true" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}/>
          <input type="password" name="fake-pass" autoComplete="new-password" tabIndex={-1} aria-hidden="true" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}/>
          <div className="overline">Sign in</div>
          <h1 className="font-display text-3xl font-extrabold mt-2">Welcome back, agent.</h1>
          <p className="text-ink-muted text-sm mt-2">Use your TARA code (TF00001) and password.</p>

          <div className="mt-8 space-y-4">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink-muted">Agent code</Label><Input data-testid="agent-login-code" name={`agent-code-${Math.random().toString(36).slice(2,8)}`} autoComplete="off" autoCorrect="off" spellCheck="false" readOnly onFocus={(e)=>e.target.removeAttribute("readonly")} value={code} onChange={e=>setCode(e.target.value.toUpperCase())} placeholder="TF00001" required/></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink-muted">Password</Label><Input data-testid="agent-login-password" name={`agent-pass-${Math.random().toString(36).slice(2,8)}`} type="password" autoComplete="new-password" readOnly onFocus={(e)=>e.target.removeAttribute("readonly")} value={password} onChange={e=>setPassword(e.target.value)} required/></div>
          </div>

          {error && <div className="mt-4 text-sm text-red-600" data-testid="agent-login-error">{error}</div>}

          <Button data-testid="agent-login-submit" type="submit" disabled={busy} className="btn-saffron rounded-full w-full mt-6 h-12">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin"/>} Sign in
          </Button>

          <div className="mt-5 text-center text-sm text-ink-muted">
            New agent? <Link to="/agent/register" className="text-saffron font-semibold">Register here</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
